import { create } from 'zustand';
import type {
  AudioCaptureMode,
  AudioLocalDisposition,
  AudioRecoveryKind,
} from '@/types/audio-recovery';
import type { ScriptureAnchor } from '@/types/prayer';
import { normalizeRecoveryKind } from '@/lib/audio-recovery';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';

/**
 * In-memory metadata mirror of the IDB `audio-queue` store. Components
 * (PendingTranscriptionPill, ResumeRecordingCard, InlineSaveRescueBanner)
 * subscribe via zustand. The full audio Blob stays in IDB —
 * only metadata flows through this store, so a queue with several
 * megabyte-sized blobs doesn't hold them in JS heap.
 *
 * Hydrated inside OwnerScopedRuntime only after the owner gate resolves.
 * After that, `audio-queue.ts`'s enqueue/dequeue/processor calls keep this
 * store in lock-step with IDB writes.
 *
 * Scope contract (account-change recording plan rev 4.1, section 7): the list
 * belongs to exactly one owner runtime, `{ runtimeId, ownerId, authEpoch }`,
 * where `runtimeId` is fresh for every OwnerScopedRuntime effect run, so a
 * StrictMode replay or a remount with the same owner and epoch is a different
 * runtime. Every list update carries the fence its operation captured at entry
 * (before any await) and applies only when that fence still equals the scope
 * and the current auth. A stale update from an earlier runtime of the same
 * owner and epoch is not applied; the item is re-read under the current scope
 * instead (`refreshItem`). Any other stale update is dropped. An old runtime
 * can never reopen, close, fill or mark hydrated a newer scope.
 */

/** The owner runtime the in-memory list currently belongs to. */
export interface QueueScope {
  runtimeId: string;
  ownerId: string;
  authEpoch: number;
}

/** A scope snapshot captured at an operation's entry; `null` when none was open. */
export type QueueFence = QueueScope | null;

/** What happened to a fenced list update. */
export type QueueListOutcome = 'applied' | 'refresh' | 'dropped';

export interface QueueItemMeta {
  id: string;
  ownerId?: string;
  authEpochAtCapture?: number;
  audioProtocolVersion?: 1;
  captureMode?: AudioCaptureMode;
  destinationId?: string;
  sourceDeviceId?: string;
  prayerId: string | null;
  mimeType: string;
  capturedAt: string;
  language: string;
  transcribedText: string | null;
  attemptCount: number;
  failed: boolean;
  sizeBytes: number;
  /**
   * V.A.2: present once the row's audio lives in Supabase Storage.
   * Absent means the row still carries its blob in IDB and processOne
   * will upload it on the next pass. Mirroring this on the metadata
   * layer is for parity with the IDB row only — no consumer branches
   * on it today, but keeping the shape symmetric prevents drift if a
   * future surface needs to know without an IDB read.
   */
  storagePath?: string;
  /**
   * V.1.1 — see db.AudioQueueRow.awaitingInlineSave. Mirrors the IDB
   * row field so consumers can derive selectors (e.g. PrayPage's
   * Save-gate via useVoiceCapture.inlineSaveAvailable) without a
   * fresh IDB read on every render.
   */
  awaitingInlineSave?: boolean;
  /**
   * Bookmarks Phase 2 — mirrors db.AudioQueueRow.inlineSaveSource.
   * The surface that produced this inline-save orphan; absent
   * normalizes to 'prayer' (see normalizeSource in audio-queue.ts).
   * Drives the prayer-only claim allowlist in useVoiceCapture.
   */
  inlineSaveSource?: 'prayer' | 'bookmark';
  /** Mirrors the exact frozen passage carried by bookmark recovery rows. */
  scriptureAnchor?: ScriptureAnchor;
  recoveryKind?: AudioRecoveryKind;
  byteSha256?: string;
  localDisposition?: AudioLocalDisposition;
  registryRevision?: string;
  registryState?: string;
  /** Explicit no-speech reply for this revision; fenced page memory only, never stored. */
  confirmedNoSpeechRevision?: string;
  destinationState?: string;
  destinationEntityType?: 'prayer' | 'bookmark';
  destinationEntityId?: string;
  destinationSubId?: string;
  destinationOpId?: string;
  recoveryLeaseToken?: string;
  recoveryLeaseExpiresAt?: string;
}

/**
 * Why a recording the queue run cannot process is waiting (LC4, operator
 * decision of 2026-10-19). Page memory only: never persisted, cleared with the
 * owner runtime's scope, and detected again on every launch before any
 * automatic write. `mismatch`: its bytes read but do not match the reference
 * recorded when it was first saved, so it is never sent. `unreadable`: its
 * bytes cannot be read in this page; the person is asked to close and reopen
 * the app so it can finish.
 */
// `missing` (LC7, review D-A): a stored pending Prayer names this queue id but
// no recording row exists on this device (an owner-checked read at hydrate).
export type RecordingAttention = 'mismatch' | 'unreadable' | 'missing';

// 2026-10-30: the former soft cap (QUEUE_CAP_COUNT / QUEUE_CAP_BYTES and the
// derived `overCap` flag) was removed. Old recordings waiting in the queue
// never block the microphone. `pendingCount` and `totalBytes` remain as
// diagnostic counters; every retained row is kept exactly as before.

/** A row with no durable transcript still needs the cloud STT path. */
export function requiresEnhancedVoiceProcessing(
  item: Pick<QueueItemMeta, 'transcribedText'>,
): boolean {
  // Persisted IDB predates this helper and is an untrusted boundary. A
  // malformed historical value must park fail-closed, never throw during a
  // React selector and take the owner workspace down.
  return (
    typeof item.transcribedText !== 'string' ||
    item.transcribedText.trim() === ''
  );
}

/**
 * Metadata-only mirror of the durable Blob/Storage-pointer test. Hydration
 * writes an exact byte count (falling back to Blob.size), while intentional
 * text-only rows use both `text/plain` and zero. Audio MIME rows remain
 * protected even when a failed/legacy Blob reports zero bytes. Missing,
 * non-finite, or negative historical metadata is unknown protected content
 * and therefore remains fail-closed.
 */
export function audioQueueMetaCarriesBytes(
  item: Partial<Pick<QueueItemMeta, 'mimeType' | 'sizeBytes' | 'storagePath'>>,
): boolean {
  return (
    item.storagePath !== undefined ||
    item.mimeType !== 'text/plain' ||
    typeof item.sizeBytes !== 'number' ||
    !Number.isFinite(item.sizeBytes) ||
    item.sizeBytes !== 0
  );
}

/** UI policy mirroring audioQueueDeletionAllowed's durable-row decision. */
export function audioQueueMetaDeletionAllowed(
  item: Pick<
    QueueItemMeta,
    | 'mimeType'
    | 'sizeBytes'
    | 'storagePath'
    | 'transcribedText'
    | 'ownerId'
    | 'audioProtocolVersion'
    | 'captureMode'
    | 'destinationId'
    | 'recoveryKind'
    | 'localDisposition'
  >,
  voiceEnabled: boolean,
): boolean {
  if (
    !item.ownerId ||
    item.audioProtocolVersion !== 1 ||
    (item.captureMode !== 'enhanced' && item.captureMode !== 'browser') ||
    !item.destinationId ||
    normalizeRecoveryKind(item.recoveryKind) === 'unknown' ||
    item.localDisposition === 'quarantined' ||
    item.localDisposition === 'deleted'
  ) return false;
  return voiceEnabled || (
    !audioQueueMetaCarriesBytes(item) &&
    !requiresEnhancedVoiceProcessing(item)
  );
}

/**
 * D4 (2026-10-18): the bound, commit-pending rows of one destination entity
 * whose transcript exists. Such a row completed its content commit locally and
 * is bound to that exact operation, but the server has not confirmed the
 * receipt (a restored device finds the registry record bound and
 * `receipt_missing`). The destination's detail shows the words read-only;
 * nothing selected here is retried, rebound, merged or removed by the client.
 */
export function unconfirmedSaveCandidates(
  items: QueueItemMeta[],
  ownerId: string,
  entity: { type: 'prayer' | 'bookmark'; id: string },
): QueueItemMeta[] {
  return items.filter((item) =>
    item.ownerId === ownerId &&
    item.audioProtocolVersion === 1 &&
    item.localDisposition === 'destination-commit-pending' &&
    item.destinationState === 'bound' &&
    item.destinationEntityType === entity.type &&
    item.destinationEntityId === entity.id &&
    typeof item.destinationOpId === 'string' &&
    Boolean(item.transcribedText?.trim()));
}

interface AudioQueueState {
  /** The owner runtime this list belongs to; null between runtimes. */
  scope: QueueScope | null;
  items: QueueItemMeta[];
  /** Derived count for callers that don't need full items array. */
  pendingCount: number;
  /** Derived total bytes across all queued blobs (diagnostic only). */
  totalBytes: number;
  hydrated: boolean;
  /** The current scope's hydrate failed (the app continues without a queue list). */
  hydrateFailed: boolean;
  /** Recordings the queue run cannot process in this page, and why (LC4; page memory only). */
  attention: Record<string, RecordingAttention>;

  /** At runtime mount: replaces any scope with an empty, unhydrated list. */
  openScope: (scope: QueueScope) => void;
  /** At runtime teardown: closes and empties the list only if `runtimeId` is current. */
  closeScope: (runtimeId: string) => void;
  /** Replace the list after a hydrate read; never creates or reopens a scope. */
  replaceItems: (fence: QueueFence, items: QueueItemMeta[]) => QueueListOutcome;
  upsertItem: (fence: QueueFence, item: QueueItemMeta) => QueueListOutcome;
  /**
   * Patch an existing item (e.g. to set transcribedText after a
   * successful processor run). No-op when id not found, so stale
   * processor writes after the user discards an entry can't resurrect
   * stale state.
   */
  updateItem: (fence: QueueFence, id: string, partial: Partial<QueueItemMeta>) => QueueListOutcome;
  removeItem: (fence: QueueFence, id: string) => QueueListOutcome;
  /**
   * Record (or clear, with null) why one recording is waiting. Applied only
   * under the current scope's own fence and auth, like every list update;
   * otherwise dropped (never re-read: the next pass detects it again).
   */
  setAttention: (fence: QueueFence, id: string, attention: RecordingAttention | null) => QueueListOutcome;
  /** Only for the current scope's runtime; a stale runtime changes nothing. */
  markHydrated: (runtimeId: string, options?: { failed?: boolean }) => boolean;
  /**
   * Re-read one row under the scope current NOW (its fence captured before
   * the read) and apply it. One shot: if that scope is replaced during the
   * read, the newer runtime's own hydrate read already follows the durable
   * write that caused this refresh, so nothing further is needed.
   */
  refreshItem: (id: string) => Promise<QueueListOutcome>;
}

/**
 * Reads one durable row as list metadata (`null` when absent). Registered by
 * `audio-queue.ts`, which owns the IndexedDB mapping, so this metadata module
 * stays free of persistence imports.
 */
type QueueItemReader = (id: string) => Promise<QueueItemMeta | null>;
let queueItemReader: QueueItemReader | null = null;

export function setQueueItemReader(reader: QueueItemReader): void {
  queueItemReader = reader;
}

function authMatchesScope(scope: QueueScope): boolean {
  return getAuthUserId() === scope.ownerId && getAuthEpoch() === scope.authEpoch;
}

function fenceOutcome(fence: QueueFence, scope: QueueScope | null): 'current' | 'refresh' | 'dropped' {
  if (!fence || !scope || !authMatchesScope(scope)) return 'dropped';
  if (fence.ownerId !== scope.ownerId || fence.authEpoch !== scope.authEpoch) return 'dropped';
  return fence.runtimeId === scope.runtimeId ? 'current' : 'refresh';
}

/**
 * Rows of the scope's owner, plus legacy rows that carry no owner at all:
 * those belong to no other account, stay inert (nothing processes or offers
 * them) and must keep counting toward the capture cap as before (rule #25).
 * A row of any other owner never enters the list.
 */
export function queueItemBelongsToScope(
  item: Pick<QueueItemMeta, 'ownerId'>,
  scope: Pick<QueueScope, 'ownerId'>,
): boolean {
  return item.ownerId === undefined || item.ownerId === null || item.ownerId === scope.ownerId;
}

const EMPTY_LIST = {
  items: [] as QueueItemMeta[],
  pendingCount: 0,
  totalBytes: 0,
  hydrated: false,
  hydrateFailed: false,
  attention: {} as Record<string, RecordingAttention>,
};

function deriveCounters(
  items: QueueItemMeta[],
  attention: Record<string, RecordingAttention>,
): Pick<AudioQueueState, 'pendingCount' | 'totalBytes'> {
  // Failed/review rows are excluded only from pendingCount. They're terminal
  // until the user resolves them via
  // PrayerDetail's TranscriptionRecoveryBanner — counting them as
  // "pending" lied in the pill ("Finishing N prayers when online")
  // while its separate review affordance remains the truthful UI lane.
  //
  // V.B.3: awaitingInlineSave rows are now also excluded. The processor
  // skips them (audio-queue.ts), so counting them as "pending" lied in
  // the pill the same way failed rows did. Their surfacing lives on
  // PrayPage's InlineSaveRescueBanner (the rescue affordance for
  // transcribe-fail-on-stop orphans the user can claim via Save), and
  // pendingCount must not double-represent the same audio across the
  // banner + pill. totalBytes is different: every retained row still counts
  // toward it, as a diagnostic of how much audio the device is holding. Since
  // 2026-10-30 that total no longer gates new capture.
  //
  // D4 (2026-10-18): commit-pending rows are excluded as well. Their remaining
  // work is a receipt check and release, not transcription; the pill must not
  // promise "Finishing N prayers". totalBytes still counts them.
  //
  // LC4 (2026-10-19): so are recordings the queue run cannot process in this
  // page (`attention`); their prayers explain why instead.
  const active = items.filter(
    (i) =>
      !i.failed &&
      !i.awaitingInlineSave &&
      !(i.recoveryKind && i.recoveryKind !== 'new-prayer' && i.transcribedText) &&
      i.localDisposition !== 'destination-commit-pending' &&
      attention[i.id] === undefined,
  );
  const pendingCount = active.length;
  const totalBytes = items.reduce((sum, item) => sum + item.sizeBytes, 0);
  return { pendingCount, totalBytes };
}

/**
 * `attention` without the `missing` mark of every id in `ids` (LC7, LC8): a row
 * with that id is in the list again, so its recording is on this device. The
 * same object when nothing changes.
 */
function withoutMissing(
  attention: Record<string, RecordingAttention>,
  ids: Iterable<string>,
): Record<string, RecordingAttention> {
  let next = attention;
  for (const id of ids) {
    if (attention[id] === 'missing') {
      if (next === attention) next = { ...attention };
      delete next[id];
    }
  }
  return next;
}

export const useAudioQueueStore = create<AudioQueueState>((set, get) => {
  /**
   * Shared fence handling: `current` applies `apply`; a stale update of the
   * same owner and epoch schedules a fenced re-read; anything else is dropped.
   * `apply` returns null when there is nothing to change, or 'foreign' when
   * the item belongs to another owner (dropped).
   */
  const fenced = (
    fence: QueueFence,
    id: string,
    apply: (state: AudioQueueState, scope: QueueScope) => Partial<AudioQueueState> | null | 'foreign',
  ): QueueListOutcome => {
    const state = get();
    const outcome = fenceOutcome(fence, state.scope);
    if (outcome === 'dropped') return 'dropped';
    if (outcome === 'refresh') {
      void get().refreshItem(id);
      return 'refresh';
    }
    const patch = apply(state, state.scope as QueueScope);
    if (patch === 'foreign') return 'dropped';
    if (patch) set(patch);
    return 'applied';
  };

  return {
    scope: null,
    ...EMPTY_LIST,

    openScope: (scope) => set({ scope: { ...scope }, ...EMPTY_LIST }),

    closeScope: (runtimeId) => {
      if (get().scope?.runtimeId !== runtimeId) return;
      set({ scope: null, ...EMPTY_LIST });
    },

    replaceItems: (fence, items) => {
      // A hydrate is its own runtime's read: only that runtime's scope takes
      // it, and it never triggers a re-read for another runtime.
      const state = get();
      if (fenceOutcome(fence, state.scope) !== 'current') return 'dropped';
      const scope = state.scope as QueueScope;
      const owned = items.filter((item) => queueItemBelongsToScope(item, scope));
      const attention = withoutMissing(state.attention, owned.map((item) => item.id));
      set({ items: owned, attention, ...deriveCounters(owned, attention) });
      return 'applied';
    },

    upsertItem: (fence, item) =>
      fenced(fence, item.id, (state, scope) => {
        if (!queueItemBelongsToScope(item, scope)) return 'foreign';
        const idx = state.items.findIndex((i) => i.id === item.id);
        const next = idx === -1
          ? [...state.items, item]
          : state.items.map((i, n) => (n === idx ? item : i));
        // A row with this id is here now, so it is no longer missing (LC7).
        const attention = withoutMissing(state.attention, [item.id]);
        return { items: next, attention, ...deriveCounters(next, attention) };
      }),

    updateItem: (fence, id, partial) =>
      fenced(fence, id, (state, scope) => {
        const idx = state.items.findIndex((i) => i.id === id);
        if (idx === -1) return null;
        const merged = { ...state.items[idx], ...partial };
        if (!queueItemBelongsToScope(merged, scope)) return 'foreign';
        const next = state.items.map((i, n) => (n === idx ? merged : i));
        return { items: next, ...deriveCounters(next, state.attention) };
      }),

    removeItem: (fence, id) =>
      fenced(fence, id, (state) => {
        const next = state.items.filter((i) => i.id !== id);
        const { [id]: _removed, ...attention } = state.attention;
        void _removed;
        return { items: next, attention, ...deriveCounters(next, attention) };
      }),

    setAttention: (fence, id, value) => {
      const state = get();
      if (fenceOutcome(fence, state.scope) !== 'current') return 'dropped';
      if ((state.attention[id] ?? null) === value) return 'applied';
      const { [id]: _previous, ...rest } = state.attention;
      void _previous;
      const attention = value === null ? rest : { ...rest, [id]: value };
      set({ attention, ...deriveCounters(state.items, attention) });
      return 'applied';
    },

    markHydrated: (runtimeId, options) => {
      // The current runtime id alone is not enough: once its scope's owner or
      // epoch is stale (sign-out, another owner, A to B to A), a completion
      // from that session marks nothing, success or failure.
      const scope = get().scope;
      if (!scope || scope.runtimeId !== runtimeId || !authMatchesScope(scope)) return false;
      set({ hydrated: true, hydrateFailed: options?.failed === true });
      return true;
    },

    refreshItem: async (id) => {
      const fence = get().scope;
      if (!fence || !queueItemReader) return 'dropped';
      let item: QueueItemMeta | null;
      try {
        item = await queueItemReader(id);
      } catch {
        return 'dropped';
      }
      // Applied only under the fence captured before the read; a refresh that
      // itself went stale is never chained into another refresh.
      if (fenceOutcome(fence, get().scope) !== 'current') return 'dropped';
      const scope = get().scope as QueueScope;
      const state = get();
      const without = state.items.filter((i) => i.id !== id);
      const next = item && queueItemBelongsToScope(item, scope)
        ? (state.items.some((i) => i.id === id)
          ? state.items.map((i) => (i.id === id ? item : i))
          : [...state.items, item])
        : without;
      // A returned row clears its missing mark (LC8).
      const attention = next.some((i) => i.id === id) ? withoutMissing(state.attention, [id]) : state.attention;
      set({ items: next, attention, ...deriveCounters(next, attention) });
      return 'applied';
    },
  };
});

/**
 * The list's scope at an operation's entry. Call it synchronously before the
 * operation's first await and pass the result to every list update the
 * operation makes, however long its asynchronous work takes.
 */
export function captureQueueListFence(): QueueFence {
  return useAudioQueueStore.getState().scope;
}

/** Non-reactive snapshot for read-only callers (e.g. queue processor). */
export function getQueueSnapshot(): AudioQueueState {
  return useAudioQueueStore.getState();
}
